import { useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { TranslationAPI } from '@/services';
import type { DetectedLanguageInfo, TranslateTabAction } from './translateTabReducer';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DETECTION_SAMPLE_SIZE = 1000;
const DETECTION_DEBOUNCE_MS = 500;
const MIN_DETECTION_LENGTH = 10;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseLanguageDetectionParams {
  sourceText: string;
  sourceLanguage: string;
  dispatch: React.Dispatch<TranslateTabAction>;
}

export function useLanguageDetection({
  sourceText,
  sourceLanguage,
  dispatch,
}: UseLanguageDetectionParams): void {
  const detectLanguage = useDebouncedCallback(async (text: string) => {
    if (sourceLanguage !== 'auto' || text.length < MIN_DETECTION_LENGTH) {
      dispatch({ type: 'SET_DETECTED_LANGUAGE_INFO', payload: null });
      return;
    }

    dispatch({ type: 'SET_IS_DETECTING', payload: true });
    try {
      const cleanedText = cleanForDetection(text);

      if (cleanedText.length < MIN_DETECTION_LENGTH) {
        dispatch({ type: 'SET_DETECTED_LANGUAGE_INFO', payload: null });
        return;
      }

      const result = await TranslationAPI.detectLanguage({ text: cleanedText });
      if (result.success) {
        const info: DetectedLanguageInfo = {
          code: result.detected_language,
          name: result.language_name,
          confidence: result.confidence,
        };
        dispatch({ type: 'SET_DETECTED_LANGUAGE_INFO', payload: info });
      }
    } catch (error) {
      console.error('Language detection failed:', error);
    } finally {
      dispatch({ type: 'SET_IS_DETECTING', payload: false });
    }
  }, DETECTION_DEBOUNCE_MS);

  useEffect(() => {
    if (sourceLanguage === 'auto' && sourceText.length >= MIN_DETECTION_LENGTH) {
      detectLanguage(sourceText);
    } else if (sourceLanguage !== 'auto') {
      dispatch({ type: 'SET_DETECTED_LANGUAGE_INFO', payload: null });
    }
  }, [sourceText, sourceLanguage, detectLanguage, dispatch]);
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function cleanForDetection(text: string): string {
  return text
    .slice(0, DETECTION_SAMPLE_SIZE * 2)
    .replace(/[\r\n]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/[^\p{L}\p{N}\s.,!?;:'"()-]/gu, '')
    .trim()
    .slice(0, DETECTION_SAMPLE_SIZE);
}
